import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { siteService } from '../../services/siteService';
import { useLanguage } from '../../contexts/LanguageContext';

import RAGProjectCard from './components/RAGProjectCard';
// import WorkerPerformanceTable from './components/WorkerPerformanceTable'; // hidden until feature is built
// import ReportBuilderPanel from './components/ReportBuilderPanel'; // hidden until feature is built
import AuditTrailFeed from './components/AuditTrailFeed';

const NAV_TABS = [
  { id: 'overview', labelKey: 'overview', icon: 'LayoutDashboard' },
  { id: 'sites', labelKey: 'sites', icon: 'Building2' },
  { id: 'documents', labelKey: 'documents', icon: 'FileText' },
  { id: 'drawings', labelKey: 'drawings', icon: 'PenTool' },
  { id: 'snags', labelKey: 'snags', icon: 'Wrench' },
  { id: 'incidents', labelKey: 'incidents', icon: 'AlertOctagon' },
  { id: 'reports', labelKey: 'reports', icon: 'FileBarChart' },
];

const RAG_FILTERS = [
  { id: 'all', labelKey: 'allProjects', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { id: 'green', labelKey: 'green', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { id: 'amber', labelKey: 'amber', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { id: 'red', labelKey: 'red', color: 'bg-red-100 text-red-800 border-red-300' },
];

const EnhancedAdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState(searchParams?.get('tab') || 'overview');
  const [ragFilter, setRagFilter] = useState('all');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const sitesResult = await siteService?.getAllSites();
      if (!sitesResult?.error) setSites(sitesResult?.data || []);
    } catch (err) {
      console.error('[EnhancedAdminDashboard] load data failed:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tabRoutes = {
      sites: '/sites',
      documents: '/document-management',
      drawings: '/drawings-plans',
      snags: '/audits-inspections',
      incidents: '/incident-reporting',
      reports: null,
    };
    if (tabId !== 'overview' && tabId !== 'reports' && tabRoutes?.[tabId]) {
      navigate(tabRoutes?.[tabId]);
    }
  };

  const filteredSites = useMemo(() => {
    if (ragFilter === 'all') return sites;
    const statusMap = { green: 'compliant', amber: 'warning', red: 'critical' };
    return sites?.filter(s => s?.compliance_status === statusMap?.[ragFilter]);
  }, [sites, ragFilter]);

  const summaryStats = useMemo(() => {
    // okzdbyNN: strictly-active — exclude archived so the KPI is not inflated.
    const total = sites?.filter(s => s?.status === 'active')?.length;
    const green = sites?.filter(s => s?.compliance_status === 'compliant')?.length;
    const amber = sites?.filter(s => s?.compliance_status === 'warning')?.length;
    const red = sites?.filter(s => s?.compliance_status === 'critical')?.length;
    const totalWorkers = sites?.reduce((sum, s) => sum + (s?.worker_count || 0), 0);
    return { total, green, amber, red, totalWorkers };
  }, [sites]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Enhanced Admin Dashboard — SiteProof</title>
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between py-5">
              <div>
                <h1 className="text-2xl font-bold text-foreground font-heading">{t('adminDashboard')}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t('welcomeBack')}, {userProfile?.full_name || t('admin')} · {t('companyWideOverview')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Package"
                  iconPosition="left"
                  onClick={() => setActiveTab('reports')}
                >
                  {t('auditPack')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => navigate('/site-setup-wizard')}
                >
                  {t('newSite')}
                </Button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center gap-1 overflow-x-auto pb-px scrollbar-hide">
              {NAV_TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab?.id
                      ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {t(tab?.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 space-y-8">

          {/* Summary KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { labelKey: 'totalProjects', value: summaryStats?.total, icon: 'Building2', color: 'text-slate-600 bg-slate-100', onClick: () => setRagFilter('all') },
              { labelKey: 'greenCompliant', value: summaryStats?.green, icon: 'CheckCircle2', color: 'text-emerald-600 bg-emerald-50', onClick: () => setRagFilter('green') },
              { labelKey: 'amberAttention', value: summaryStats?.amber, icon: 'AlertTriangle', color: 'text-amber-600 bg-amber-50', onClick: () => setRagFilter('amber') },
              { labelKey: 'redCritical', value: summaryStats?.red, icon: 'XCircle', color: 'text-red-600 bg-red-50', onClick: () => setRagFilter('red') },
              { labelKey: 'totalWorkers', value: summaryStats?.totalWorkers, icon: 'Users', color: 'text-blue-600 bg-blue-50', onClick: () => navigate('/workers') },
            ]?.map(card => (
              <button
                key={card?.labelKey}
                onClick={card?.onClick}
                className="bg-card rounded-xl border border-border p-4 text-left hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card?.color}`}>
                    <Icon name={card?.icon} size={18} />
                  </div>
                  <Icon name="ArrowUpRight" size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-2xl font-bold text-foreground font-heading">{card?.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t(card?.labelKey)}</p>
              </button>
            ))}
          </div>

          {/* RAG Project Cards */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground font-heading">{t('projectRagStatus')}</h2>
                <p className="text-sm text-muted-foreground">{t('clickCardToDrillDown')}</p>
              </div>
              <div className="flex items-center gap-2">
                {RAG_FILTERS?.map(f => (
                  <button
                    key={f?.id}
                    onClick={() => setRagFilter(f?.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      ragFilter === f?.id ? f?.color + ' ring-2 ring-offset-1 ring-current' : 'bg-background text-muted-foreground border-border hover:border-slate-300'
                    }`}
                  >
                    {t(f?.labelKey)}
                    {f?.id !== 'all' && (
                      <span className="ml-1.5">
                        {f?.id === 'green' ? summaryStats?.green : f?.id === 'amber' ? summaryStats?.amber : summaryStats?.red}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {filteredSites?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border p-12 text-center">
                <Icon name="Building2" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">{t('noProjectsMatchFilter')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredSites?.map(site => (
                  <RAGProjectCard key={site?.id} site={site} />
                ))}
              </div>
            )}
          </section>

          {/* Worker Performance — hidden until feature is built */}
          {/* <section>
            <WorkerPerformanceTable onWorkerClick={(workerId) => navigate(`/workers/${workerId}`)} />
          </section> */}

          {/* Audit Trail */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <AuditTrailFeed />
            </div>
            {/* Report Builder — hidden until feature is built */}
            {/* <div>
              <ReportBuilderPanel sites={sites} />
            </div> */}
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={18} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EnhancedAdminDashboard;
